<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="box error" v-if="!configUI['project-annotations-tab']">
  <h2> {{ $t('access-denied') }} </h2>
  <p>{{ $t('insufficient-permission') }}</p>
</div>
<div class="box error" v-else-if="error">
  <h2> {{ $t('error') }} </h2>
  <p>{{ $t('error-load-annotations-filters') }}</p>
</div>
<div v-else class="annots-page">
  <b-loading :is-full-page="false" :active="loading" />
  <template v-if="!loading">

    <!-- active filter chips -->
    <div v-if="chips.length" class="az-chips">
      <span class="az-chips-label">{{ $t('active-filters') }}</span>
      <span v-for="(ch, i) in chips" :key="i" class="az-chip">
        {{ ch.label }}
        <span class="az-chip-x" @click="ch.onRemove"><i class="fas fa-times"></i></span>
      </span>
    </div>

    <!-- sticky filter bar: filters live behind a button (design) -->
    <div class="az-bar">
      <button class="az-filters-btn" @click="popoverOpen = true">
        <i class="fas fa-sliders-h"></i>
        {{ $t('filters') }}
        <span v-if="chips.length" class="az-badge">{{ chips.length }}</span>
      </button>
      <button class="az-reset-btn" @click="resetFilters">
        <i class="fas fa-undo"></i> {{ $t('reset') }}
      </button>
      <div class="az-bar-div"></div>

      <!-- inline facet dropdowns (bound to the same filter state as the popover) -->
      <div class="az-bar-strip" v-click-outside="closeBar">
        <div v-for="f in barFacets" :key="f.key" class="az-bardd">
          <button class="az-bardd-btn" :class="{open: openBar === f.key}" @click="toggleBarOpen(f.key)">
            <span class="az-bardd-label">{{ f.label }}</span>
            <span class="az-bardd-pill" :class="{some: barMasterState(f.key) === 'some'}">{{ barSummary(f.key) }}</span>
            <i class="fas fa-chevron-down az-bardd-caret"></i>
          </button>
          <div v-if="openBar === f.key" class="az-bardd-pop">
            <div class="az-bardd-search">
              <i class="fas fa-search"></i>
              <input v-model="barSearch" :placeholder="`${$t('search')}…`" />
            </div>

            <!-- case-info finder (Images only) -->
            <div v-if="f.key === 'images'" class="az-case">
              <div class="az-case-head" @click="caseFilterOpen = !caseFilterOpen">
                <i class="fas fa-filter"></i>
                <span>{{ $t('case-information') }}</span>
                <span v-if="caseActive" class="az-case-active">{{ caseCriteria.length }}</span>
                <span class="az-bar-spacer"></span>
                <i class="fas" :class="caseFilterOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </div>
              <div v-if="caseFilterOpen" class="az-case-body">
                <div class="az-case-row">
                  <div class="select is-small">
                    <select v-model="caseField">
                      <option v-for="cf in caseFields" :key="cf.val" :value="cf.val">{{ cf.label }}</option>
                    </select>
                  </div>
                  <input class="az-case-val" v-model="caseValue" :placeholder="`${$t('value')}…`"
                         @keydown.enter.prevent="addCaseCriterion" />
                  <button class="az-case-add" @click="addCaseCriterion"><i class="fas fa-plus"></i></button>
                </div>
                <div v-if="caseCriteria.length" class="az-case-chips">
                  <span v-for="(c, i) in caseCriteria" :key="i" class="az-case-chip">
                    <span class="k">{{ caseFieldLabel(c.field) }}:</span>{{ c.value }}
                    <span class="x" @click="removeCaseCriterion(i)"><i class="fas fa-times"></i></span>
                  </span>
                </div>
                <div v-if="caseLoading" class="az-case-loading"><i class="fas fa-spinner fa-spin"></i></div>
              </div>
            </div>

            <div v-if="!barSearch && !(f.key === 'images' && caseActive)" class="az-opt az-master" @click="barMaster(f.key)">
              <span class="az-check" :class="barMasterState(f.key)"></span>
              <span class="az-opt-name">{{ $t('all') }} {{ f.label }}</span>
            </div>
            <div v-for="o in barOptions(f.key)" :key="o.id" class="az-opt" @click="barToggle(f.key, o.id)">
              <span class="az-check" :class="barChecked(f.key, o.id) ? 'all' : 'none'"></span>
              <span class="az-opt-text">
                <span class="az-opt-name">{{ o.name }}</span>
                <span v-if="o.sub" class="az-opt-sub">{{ o.sub }}</span>
              </span>
              <span v-if="facetCountText(f.key, o.id) !== ''" class="az-opt-count">{{ facetCountText(f.key, o.id) }}</span>
            </div>
            <div v-if="barOptions(f.key).length === 0" class="az-opt-empty">{{ $t('no-result') }}</div>
          </div>
        </div>
      </div>

      <div class="az-bar-spacer"></div>
      <span class="az-count">{{ countLabel }}</span>
    </div>

    <!-- annotation groups (design grid; isolated from the viewer) -->
    <div class="az-groups" @scroll="scrollHandler" ref="listAnnots">
      <annotation-grid v-for="prop in limitedCategoryOptions" :key="`${selectedCategorization.categorization}${prop.id}`"
        :categorization="selectedCategorization.categorization"
        :size="selectedSize.size"
        :outlineColor="selectedColor.hexaCode"
        :nbPerPage="nbPerPage"

        :allTerms="terms"
        :allUsers="allUsers"
        :allImages="images"
        :allTracks="tracks"
        :allTags="tags"
        :blindMode="blindMode"

        :prop="prop"
        :multiple-terms="(isByTerm && prop.id === multipleTermsOption.id)"
        :no-term="(isByTerm && prop.id === noTermOption.id) || (!isByTerm && noTerm)"
        :multiple-tracks="(isByTrack && prop.id === multipleTracksOption.id)"
        :no-track="(isByTrack && prop.id === noTrackOption.id) || (!isByTrack && noTrack)"
        :terms-ids="selectedTermsIds"
        :tracks-ids="selectedTracksIds"
        :tags-ids="selectedTagsIds"
        :no-tag="(isByTag && prop.id === noTagOption.id) || (!isByTag && noTag)"
        :imagesIds="(isByImageGroup) ? imagesIdsInGroup(prop) : selectedImagesIds"
        :usersIds="selectedUsersIds"
        :reviewed="reviewed"
        :reviewUsersIds="reviewUsersIds"
        :afterThan="afterThan"
        :beforeThan="beforeThan"
        :revision="revision"
        :selectedIds="selectionIds"

        v-show="showList(prop)"
        :visible="showList(prop)"

        @open="openLightbox({annot: $event})"
        @slide="viewAnnot({annot: $event})"
        @delete="deleteAnnotation($event)"
        @toggle="toggleSelect($event)"
        @group-toggle="toggleGroupSelect($event)"
      />
      <button class="az-loadmore" v-if="!areAllOptionsLoaded" @click="loadCategories()">
        <span class="icon"><i class="fas fa-sync"></i></span>
        <span>{{$t('button-load-more')}}</span>
      </button>

      <!-- nothing to show yet: filters start empty so the page opens calm -->
      <div v-if="!loading && visibleGroups.length === 0" class="az-empty-hint">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.7"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
        <p class="az-empty-title">{{ $t('select-filters-to-view-annotations') }}</p>
      </div>
    </div>

    <!-- export results -->
    <div class="az-export">
      <div class="az-export-info">
        <span class="az-export-ico"><i class="fas fa-download"></i></span>
        <div>
          <div class="az-export-title">{{ $t('download-results') }}</div>
          <div class="az-export-sub">{{ countLabel }}</div>
        </div>
      </div>
      <div class="az-export-menu" v-click-outside="() => exportOpen = false">
        <button class="az-export-btn" @click="exportOpen = !exportOpen" :disabled="exporting">
          <i class="fas" :class="exporting ? 'fa-spinner fa-spin' : 'fa-download'"></i>
          {{ exporting ? $t('exporting') : $t('export') }}
          <i class="fas fa-chevron-down az-export-caret"></i>
        </button>
        <div v-if="exportOpen" class="az-export-pop">
          <button class="az-fmt az-pdf" @click="download('pdf'); exportOpen = false">PDF</button>
          <button class="az-fmt az-csv" @click="download('csv'); exportOpen = false">CSV</button>
          <button class="az-fmt az-xls" @click="download('xls'); exportOpen = false">XLS</button>
        </div>
      </div>
    </div>

    <!-- ===== FILTERS POPOVER ===== -->
    <template v-if="popoverOpen">
      <div class="az-pop-scrim" @click="popoverOpen = false"></div>
      <div class="az-pop">
        <div class="az-pop-head">
          <i class="fas fa-sliders-h az-pop-head-ico"></i>
          <h2>{{ $t('refine-annotations') }}</h2>
          <button class="az-pop-reset" @click="resetFilters">{{ $t('reset-filters') }}</button>
          <button class="az-pop-x" @click="popoverOpen = false"><i class="fas fa-times"></i></button>
        </div>

        <div class="az-pop-body">
          <div class="az-section-label">{{ $t('display') }}</div>
          <div class="az-grid">
            <div class="az-field">
              <label>{{$t('categorization')}}</label>
              <ims-multiselect v-model="selectedCategorization" :options="allowedCategorizations"
                label="label" track-by="categorization" :allow-empty="false" :searchable="false" />
            </div>
            <div class="az-field">
              <label>{{$t('preview-size')}}</label>
              <ims-multiselect v-model="selectedSize" :options="allowedSizes"
                label="label" track-by="size" :allow-empty="false" :searchable="false" />
            </div>
            <div class="az-field">
              <label>{{$t('number-per-category')}}</label>
              <ims-multiselect v-model="nbPerPage" :options="[10, 25, 50, 100]"
                :allow-empty="false" :searchable="false" />
            </div>
            <div class="az-field">
              <label>{{$t('outline-color')}}</label>
              <ims-multiselect v-model="selectedColor" :options="colors"
                label="label" track-by="name" :allow-empty="false" :searchable="false" />
            </div>
            <div class="az-field">
              <label>{{$t('group-linked-annotations')}}</label>
              <ims-multiselect v-model="regroup" :options="groupBundling"
                label="label" track-by="bundling" :allow-empty="false" :searchable="false" />
            </div>
          </div>

          <div class="az-divider"></div>

          <div class="az-section-label">{{ $t('filters') }}</div>
          <div class="az-showbar">
            <span class="az-showbar-label">{{ $t('show-in-filter-bar') }}</span>
            <span class="az-showbar-tog locked"><i class="fas fa-lock"></i> {{ $t('terms') }}</span>
            <span class="az-showbar-tog locked"><i class="fas fa-lock"></i> {{ $t('images') }}</span>
            <span class="az-showbar-tog locked"><i class="fas fa-lock"></i> {{ reviewed ? $t('reviewers') : $t('members') }}</span>
            <button class="az-showbar-tog" :class="{on: extraBar.includes('tracks')}" @click="toggleExtraBar('tracks')">
              <i class="fas" :class="extraBar.includes('tracks') ? 'fa-check' : 'fa-plus'"></i> {{ $t('tracks') }}
            </button>
            <button class="az-showbar-tog" :class="{on: extraBar.includes('tags')}" @click="toggleExtraBar('tags')">
              <i class="fas" :class="extraBar.includes('tags') ? 'fa-check' : 'fa-plus'"></i> {{ $t('tags') }}
            </button>
          </div>
          <div class="az-grid">
            <div class="az-field">
              <label>{{$t('annotation-type')}}</label>
              <ims-multiselect v-model="selectedAnnotationType" :options="annotationTypes"
                :allow-empty="false" :searchable="false" />
            </div>
            <div v-if="ontology" class="az-field">
              <label>{{$t('terms')}}</label>
              <ontology-tree-multiselect :ontology="ontology" :additionalNodes="additionalTermNodes" v-model="selectedTermsIds" />
            </div>
            <div v-if="tracks" class="az-field">
              <label>{{$t('tracks')}}</label>
              <track-tree-multiselect :tracks="filteredTracks" :additional-nodes="additionalTrackNodes" v-model="selectedTracksIds" />
            </div>
            <div class="az-field">
              <label>{{$t('tags')}}</label>
              <ims-multiselect v-model="selectedTags" :options="tagsOptions"
                label="name" track-by="id" :multiple="true" :allPlaceholder="$t('all')" />
            </div>
            <div v-if="selectedAnnotationType === userAnnotationOption" class="az-field">
              <label>{{$t('members')}}</label>
              <ims-multiselect v-model="selectedMembers" :options="filteredMembers" label="fullName" track-by="id" multiple />
            </div>
            <div v-else class="az-field">
              <label>{{$t('reviewers')}}</label>
              <ims-multiselect v-model="selectedReviewers" :options="members" label="fullName" track-by="id" multiple />
            </div>
            <div class="az-field" v-if="isByImageGroup">
              <label>{{$t('image-groups')}}</label>
              <ims-multiselect v-model="selectedImageGroups" :options="imageGroups" :label="'name'"
                track-by="id" multiple :allPlaceholder="$t('all-image-groups')" />
            </div>
            <div class="az-field" v-else>
              <label>{{$t('images')}}</label>
              <b-input v-if="tooManyImages && selectedImages.length === 0" :placeholder="$t('all-images')" disabled />
              <ims-multiselect v-else v-model="selectedImages" :options="images"
                :label="blindMode ? 'blindedName' : 'instanceFilename'" track-by="id" multiple :allPlaceholder="$t('all-images')" />
            </div>
            <div class="az-field">
              <label>{{$t('from')}}</label>
              <ims-datepicker v-model="fromDate" :styles="['multiselect']" :maxDate="toDate || new Date()" />
            </div>
            <div class="az-field">
              <label>{{$t('to')}}</label>
              <ims-datepicker v-model="toDate" :styles="['multiselect']" :minDate="fromDate" position="is-bottom-left" />
            </div>
          </div>
        </div>

        <div class="az-pop-foot">
          <button class="az-pop-reset2" @click="resetFilters">{{ $t('reset-filters') }}</button>
          <div class="az-bar-spacer"></div>
          <button class="az-pop-done" @click="popoverOpen = false">{{ $t('apply') }}</button>
        </div>
      </div>

      <!-- blocking overlay while the PDF export is being generated -->
      <div v-if="exporting" class="az-export-overlay">
        <div class="az-export-card">
          <i class="fas fa-circle-notch fa-spin"></i>
          <div class="az-export-overlay-title">{{ $t('exporting') }}</div>
          <div class="az-export-overlay-sub">{{ $t('export-in-progress-hint') }}</div>
        </div>
      </div>
    </template>

    <!-- ===== LIGHTBOX (crop popup) ===== -->
    <div v-if="lightbox" class="az-light-scrim" @click="closeLightbox">
      <div class="az-light" @click.stop>
        <div class="az-light-img">
          <img v-if="lightboxCrop" :src="lightboxCrop" alt="annotation" />
          <div v-else class="az-light-spin"><i class="fas fa-spinner fa-spin"></i></div>
        </div>
        <div class="az-light-side">
          <div class="az-light-head">
            <div class="az-light-title">{{ $t('annotation') }} #{{ lightbox.id }}</div>
            <button class="az-light-x" @click="closeLightbox"><i class="fas fa-times"></i></button>
          </div>
          <div class="az-light-meta">
            <div v-if="lightbox.area > 0" class="row">
              <span class="k">{{ $t('area') }}</span>
              <span class="v">{{ lightbox.area.toFixed(3) }} {{ lightbox.areaUnit }}</span>
            </div>
            <div v-if="lightbox.perimeter > 0" class="row">
              <span class="k">{{ $t(lightbox.area > 0 ? 'perimeter' : 'length') }}</span>
              <span class="v">{{ lightbox.perimeter.toFixed(3) }} {{ lightbox.perimeterUnit }}</span>
            </div>
            <div v-if="lightboxImageName" class="row">
              <span class="k">{{ $t('image') }}</span>
              <span class="v">{{ lightboxImageName }}</span>
            </div>
            <div v-if="lightbox.created" class="row">
              <span class="k">{{ $t('created-on') }}</span>
              <span class="v">{{ Number(lightbox.created) | moment('ll') }}</span>
            </div>
          </div>
          <div class="az-light-foot">
            <button class="az-light-del" @click="deleteFromLightbox">
              <i class="fas fa-trash"></i> {{ $t('button-delete') }}
            </button>
            <button class="az-light-open" @click="openInSlide">
              <i class="fas fa-external-link-alt"></i> {{ $t('open-in-slide') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== BULK SELECTION ACTION BAR ===== -->
    <div v-if="selectionCount > 0" class="az-selbar">
      <span class="az-selcount">{{ selectionCount }} {{ $t('selected') }}</span>
      <span class="az-seldiv"></span>

      <!-- Add a term (popover wraps the trigger so the opening click isn't read as "outside") -->
      <div class="az-selwrap" v-click-outside="() => setTermOpen = false">
        <button class="az-selact" @click="setTermOpen = !setTermOpen; tagOpen = false; bulkExportOpen = false">
          <i class="fas fa-plus"></i> {{ $t('add-term') }}
        </button>
        <div v-if="setTermOpen" class="az-selpop">
          <div class="az-selpop-title">{{ singleSelectionTerm ? $t('terms') : $t('add-term') }}</div>
          <div v-for="t in setTermOptions" :key="t.id" class="az-selpop-row" @click="bulkSetTerm(t)">
            <span class="az-selpop-dot" :style="{background: t.color}"></span>{{ t.name }}
          </div>
          <div v-if="!setTermOptions.length" class="az-selpop-empty">{{ $t('no-result') }}</div>
        </div>
      </div>

      <!-- Tags -->
      <div class="az-selwrap" v-click-outside="() => tagOpen = false">
        <button class="az-selact" @click="tagOpen = !tagOpen; setTermOpen = false; bulkExportOpen = false">
          <i class="fas fa-tag"></i> {{ $t('tags') }}
        </button>
        <div v-if="tagOpen" class="az-selpop">
          <div class="az-selpop-title">{{ $t('tags') }}</div>
          <div v-for="t in tags" :key="t.id" class="az-selpop-row" @click="bulkAddTag(t)">{{ t.name }}</div>
          <div v-if="!tags.length" class="az-selpop-empty">{{ $t('no-result') }}</div>
        </div>
      </div>

      <!-- Export the selection (choose a format) -->
      <div class="az-selwrap" v-click-outside="() => bulkExportOpen = false">
        <button class="az-selact" :disabled="exporting"
                @click="bulkExportOpen = !bulkExportOpen; setTermOpen = false; tagOpen = false">
          <i class="fas" :class="exporting ? 'fa-spinner fa-spin' : 'fa-download'"></i> {{ $t('export') }}
        </button>
        <div v-if="bulkExportOpen" class="az-selpop">
          <div class="az-selpop-title">{{ $t('export') }}</div>
          <div class="az-selpop-row" @click="bulkExport('pdf')">PDF</div>
          <div class="az-selpop-row" @click="bulkExport('csv')">CSV</div>
          <div class="az-selpop-row" @click="bulkExport('xls')">XLS</div>
        </div>
      </div>

      <button class="az-selact danger" @click="bulkDelete">
        <i class="fas fa-trash"></i> {{ $t('button-delete') }}
      </button>
      <span class="az-seldiv"></span>
      <button class="az-selx" :title="$t('clear-all')" @click="clearSelection"><i class="fas fa-times"></i></button>
    </div>

  </template>
</div>
</template>

<script>
import {get, sync, syncMultiselectFilter} from '@/utils/store-helpers';
import constants from '@/utils/constants.js';

import ImsMultiselect from '@/components/form/ImsMultiselect';
import ImsDatepicker from '@/components/form/ImsDatepicker';
import OntologyTreeMultiselect from '@/components/ontology/OntologyTreeMultiselect';

import AnnotationGrid from './AnnotationGrid';

import {Ims, ImageInstanceCollection, UserCollection, AnnotationCollection, TrackCollection, TagCollection, ImageInstance, ImageGroupCollection, AnnotationTerm, TagDomainAssociation} from '@/api';

import {defaultColors} from '@/utils/style-utils.js';
import TrackTreeMultiselect from '@/components/track/TrackTreeMultiselect';

import _ from 'lodash';

// store options to use with store helpers to target projects/currentProject/listImages module
const storeOptions = {rootModuleProp: 'storeModule'};
// redefine helpers to use storeOptions and correct module path
const localSyncMultiselectFilter = (filterName, options) => syncMultiselectFilter(null, filterName, options, storeOptions);
import {appendShortTermToken} from '@/utils/token-utils.js';
const categoryBatch = constants.CATEGORY_ITEMS_PER_BATCH;

export default {
  name: 'list-annotations',
  components: {
    TrackTreeMultiselect,
    ImsMultiselect,
    ImsDatepicker,
    OntologyTreeMultiselect,
    AnnotationGrid
  },
  data() {
    return {
      loading: true,
      error: false,
      revision: 0,

      // UI-9 redesign: filters live behind a button (popover), a crop lightbox on tile
      // click, and a compact export popup.
      popoverOpen: false,
      exportOpen: false,
      exporting: false,
      lightbox: null,     // the annotation whose crop is shown in the lightbox
      lightboxCrop: null, // object URL of the fetched crop image

      // UI-10: inline filter-bar dropdowns (Terms/Images/Members) bound to the same
      // store-synced filter state as the popover, plus a case-info image finder and
      // smart-capped faceted counts.
      openBar: null,         // currently open bar dropdown key, or null
      barSearch: '',
      extraBar: [],          // optional facets shown in the bar: subset of ['tracks','tags']
      caseFilterOpen: false,
      caseField: 'caseId',
      caseValue: '',
      caseCriteria: [],      // [{field, value}]
      caseMatchedImages: null, // images matched by case-info (null = no case filter active)
      caseLoading: false,
      facetCounts: {},       // { '<facet>:<id>': totalNbItems }
      facetToken: 0,

      // UI-10 Group B: cross-group selection + bulk actions.
      selectionMap: {},      // id -> annotation object
      setTermOpen: false,
      tagOpen: false,
      bulkExportOpen: false,
      caseFields: [
        {val: 'caseId', label: this.$t('case-information-field-case-id')},
        {val: 'slideId', label: this.$t('case-information-field-slide-id')},
        {val: 'slideBlockId', label: this.$t('case-information-field-block-id')},
        {val: 'slideExternalId', label: this.$t('case-information-field-slide-external-id')},
        {val: 'specimenId', label: this.$t('case-information-field-specimen-id')}
      ],

      projectUsers: [],

      tracks: [],

      allowedSizes: [
        {label: this.$t('small'), size: 85},
        {label: this.$t('medium'), size: 125},
        {label: this.$t('large'), size: 200},
        {label: this.$t('huge'), size: 400},
      ],

      groupBundling: [
        {label: this.$t('yes-one-group-per-line'), bundling: 'ONE_PER_LINE'},
        {label: this.$t('yes'), bundling: 'YES'},
        {label: this.$t('no'), bundling: 'NO'}
      ],

      userAnnotationOption: this.$t('user-annotations'),
      reviewedAnnotationOption: this.$t('reviewed-annotations'),
      annotationTypes: [],

      images: [],
      imageGroups: [],
      tags:[],

      noTermOption: {id: 0, name: this.$t('no-term')},
      multipleTermsOption: {id: -1, name: this.$t('multiple-terms')},

      noTrackOption: {id: 0, name: this.$t('no-track')},
      multipleTracksOption: {id: -1, name: this.$t('multiple-tracks')},

      noTagOption: {id: 0, name: this.$t('no-tag')},

      uncategorizedOption: {id: 0, name: this.$t('uncategorized')},

      nLoadedOptionsPerCategory: {
        'TERM': constants.ANNOTATIONS_MAX_ITEMS_PER_CATEGORY,
        'IMAGE': constants.ANNOTATIONS_MAX_ITEMS_PER_CATEGORY,
        'USER': constants.ANNOTATIONS_MAX_ITEMS_PER_CATEGORY,
        'TRACK': constants.ANNOTATIONS_MAX_ITEMS_PER_CATEGORY,
        'UNCATEGORIZED': constants.ANNOTATIONS_MAX_ITEMS_PER_CATEGORY
      },
    };
  },
  computed: {
    allowedCategorizations() {
      let categorizations = [
        {label: this.$t('per-term'), categorization: 'TERM'},
        {label: this.$t('per-track'), categorization: 'TRACK'},
        {label: this.$t('per-user'), categorization: 'USER'},
        {label: this.$t('per-image-group'), categorization: 'IMAGEGROUP'},
      ];

      if (!this.tooManyImages) {
        categorizations.push({label: this.$t('per-image'), categorization: 'IMAGE'});
      }

      // Adding an uncategorized option at the end of the array
      categorizations.push({label: this.$t('uncategorized'), categorization: 'UNCATEGORIZED'});

      return categorizations;
    },
    currentUser: get('currentUser/user'),
    project: get('currentProject/project'),
    shortTermToken: get('currentUser/shortTermToken'),
    blindMode() {
      return this.project.blindMode;
    },
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    ontology: get('currentProject/ontology'),
    configUI: get('currentProject/configUI'),

    storeModule() { // path to the vuex module in which state of this component is stored (projects/currentProject/listAnnotations)
      return this.$store.getters['currentProject/currentProjectModule'] + 'listAnnotations';
    },

    colors() {
      let colors = defaultColors.map(color => ({label: this.$t(color.name), ...color}));
      colors.push({label: this.$t('no-outline'), hexaCode: ''});
      return colors;
    },

    selectedSize: sync('previewSize', storeOptions),
    selectedCategorization: sync('categorization', storeOptions),
    nbPerPage: sync('perPage', storeOptions),
    selectedColor: sync('outlineColor', storeOptions),

    // eslint-disable-next-line vue/return-in-computed-property
    targetAnnotationType() {
      switch (this.$route.query.type) {
        case 'user':
          return this.userAnnotationOption;
        case 'reviewed':
          return this.reviewedAnnotationOption;
      }
    },
    allUsers() {
      return this.projectUsers;
    },
    members: get('currentProject/members'),
    managers: get('currentProject/managers'),
    filteredMembers() { // filter the members so as to return only those whose annotations can be seen by current user
      if (this.canManageProject || (!this.project.hideUsersLayers && !this.project.hideAdminsLayers)) {
        return this.members;
      }

      let idManagers = this.managers.map(m => m.id);
      return this.members.filter(member => {
        if (this.currentUser.id === member.id) {
          return true;
        }
        let isManager = idManagers.includes(member.id);
        return isManager ? !this.project.hideAdminsLayers : !this.project.hideUsersLayers;
      });
    },

    terms() {
      return this.$store.getters['currentProject/terms'] || [];
    },
    additionalTermNodes() {
      let additionalNodes = [this.noTermOption];
      if (this.terms.length > 1 && this.isByTerm) {
        additionalNodes.push(this.multipleTermsOption);
      }
      return additionalNodes;
    },
    termsOptions() {
      return this.terms.concat(this.additionalTermNodes);
    },
    termOptionsIds() {
      return this.termsOptions.map(option => option.id);
    },
    filteredTracks() {
      return this.tracks.filter(track => this.selectedImagesIds.includes(track.image));
    },
    filteredTracksIds() {
      return this.filteredTracks.map(track => track.id);
    },
    additionalTrackNodes() {
      let additionalNodes = [this.noTrackOption];
      if (this.tracks.length > 1 && this.isByTrack) {
        additionalNodes.push(this.multipleTracksOption);
      }
      return additionalNodes;
    },
    tracksOptions() {
      return this.filteredTracks.concat(this.additionalTrackNodes);
    },
    trackOptionsIds() {
      return this.tracksOptions.map(option => option.id);
    },

    tagsOptions() {
      return [...this.tags, this.noTagOption];
    },

    selectedAnnotationType: sync('annotationType', storeOptions),
    selectedMembers: localSyncMultiselectFilter('members', 'filteredMembers'),
    selectedReviewers: localSyncMultiselectFilter('reviewers', 'members'),
    selectedImages: localSyncMultiselectFilter('images', 'images'),
    selectedImageGroups: localSyncMultiselectFilter('imageGroups', 'imageGroups'),
    selectedTags: localSyncMultiselectFilter('tags', 'tagsOptions'),
    selectedTracksIds: localSyncMultiselectFilter('tracksIds', 'trackOptionsIds'),
    selectedTermsIds: localSyncMultiselectFilter('termsIds', 'termOptionsIds'),
    fromDate: sync('fromDate', storeOptions),
    toDate: sync('toDate', storeOptions),
    regroup: sync('regroup', storeOptions),

    afterThan() {
      return this.fromDate ? this.fromDate.getTime() : null;
    },
    beforeThan() {
      return this.toDate ? this.toDate.setHours(23, 59, 59, 999) : null;
    },

    reviewed() {
      return this.selectedAnnotationType === this.reviewedAnnotationOption;
    },
    selectedUsersIds() {
      if (this.reviewed) {
        return null;
      }
      let users = this.selectedMembers;
      return users.map(user => user.id);
    },
    reviewUsersIds() {
      return this.reviewed ? this.selectedReviewers.map(u => u.id) : null;
    },

    querySearchImage() {
      return this.$route.query.image;
    },
    querySearchTags() {
      return this.$route.query.tags;
    },
    selectedImagesIds() {
      return this.selectedImages.map(img => img.id);
    },

    selectedImageGroupsIds() {
      return this.selectedImageGroups.map(group => group.id);
    },

    // eslint-disable-next-line vue/return-in-computed-property
    /**
     * This computed property returns an array.
     * The array will be either empty or will be multiple objects of the same type.
     * The type of these objects depends on the selected categorization.
     */
    categoryOptions() {
      switch (this.selectedCategorization.categorization) {
        case 'TERM':
          return this.termsOptions;
        case 'IMAGE':
          return this.images;
        case 'USER':
          if (this.reviewed) {
            return this.selectedReviewers;
          }
          return this.selectedMembers;
        case 'TRACK':
          return this.tracksOptions;
        case 'IMAGEGROUP':
          return this.imageGroups;
        case 'UNCATEGORIZED':
          // Return an array containing one option to stick to the current behavior
          return [this.uncategorizedOption];
        default:
          return [];
      }
    },
    /**
     * In the template, we loop over the limitedCategoryOptions computed property.
     * Zero, one or multiple ListAnnotationBy component can be rendered from this array.
     * The array is built by slicing the categoryOptions computed property.
     */
    limitedCategoryOptions() {
      return this.categoryOptions.slice(0, this.nLoadedOptionsPerCategory[this.selectedCategorization.categorization]);
    },
    areAllOptionsLoaded() {
      return this.categoryOptions.length === this.limitedCategoryOptions.length;
    },
    isByTerm() {
      return this.selectedCategorization.categorization === 'TERM';
    },
    isByTrack() {
      return this.selectedCategorization.categorization === 'TRACK';
    },
    isByTag() {
      return this.selectedCategorization.categorization === 'TAG';
    },
    isByImageGroup() {
      return this.selectedCategorization.categorization === 'IMAGEGROUP';
    },
    noTerm() {
      return this.selectedTermsIds.includes(this.noTermOption.id);
    },
    noTrack() {
      return this.selectedTracksIds.includes(this.noTrackOption.id);
    },
    noTag() {
      return this.selectedTagsIds.includes(this.noTagOption.id);
    },

    selectedTagsIds() {
      return this.selectedTags.map(t => t.id);
    },
    collection() {
      // An empty selection means "no constraint" (not "match none"), so picking only a term
      // still returns results even though Images/Members are left untouched.
      let imagesIds = (this.selectedImages.length === 0) ? null : this.selectedImagesIds;
      if (this.isByImageGroup) {
        imagesIds = this.selectedImageGroups.map(ig => this.imagesIdsInGroup(ig)).flat();
      }

      let collection = new AnnotationCollection({
        project: this.project.id,
        // empty terms → export nothing (mirrors the empty page); all terms → no constraint
        terms: this.selectedTermsIds.length === this.termsOptions.length ? null : this.selectedTermsIds,
        images: imagesIds,
        users: (this.selectedUsersIds && this.selectedUsersIds.length) ? this.selectedUsersIds : null,
        reviewed: this.reviewed,
        reviewUsers: this.reviewUsersIds,
        noTerm: this.noTerm,
        multipleTerms: this.selectedTermsIds.includes(this.multipleTermsOption.id),
        afterThan: this.afterThan,
        beforeThan: this.beforeThan
      });

      if (this.selectedTagsIds.length > 0 && this.selectedTagsIds.length < this.tags.length) {
        collection['tags'] = this.selectedTagsIds;
        collection['noTag'] = this.noTag;
      }

      return collection;
    },
    tooManyImages() {
      return this.project.numberOfImages > constants.MAX_IMAGES_FOR_FILTER;
    },

    // The currently displayed category groups (mirrors v-show="showList(prop)").
    visibleGroups() {
      return this.limitedCategoryOptions.filter(prop => this.showList(prop));
    },
    countLabel() {
      return `${this.visibleGroups.length} ${this.$t('groups')}`;
    },
    // Active-filter chips shown above the bar. Each chip resets its filter to "all".
    chips() {
      let chips = [];
      if (this.selectedAnnotationType === this.reviewedAnnotationOption) {
        chips.push({label: this.reviewedAnnotationOption, onRemove: () => {
          this.selectedAnnotationType = this.userAnnotationOption;
        }});
      }
      if (this.selectedTermsIds.length < this.termOptionsIds.length) {
        chips.push({label: this.$t('terms'), onRemove: () => {
          this.selectedTermsIds = [...this.termOptionsIds];
        }});
      }
      if (this.tracks.length && this.selectedTracksIds.length < this.trackOptionsIds.length) {
        chips.push({label: this.$t('tracks'), onRemove: () => {
          this.selectedTracksIds = [...this.trackOptionsIds];
        }});
      }
      if (this.selectedTags.length < this.tagsOptions.length) {
        chips.push({label: this.$t('tags'), onRemove: () => {
          this.selectedTags = [...this.tagsOptions];
        }});
      }
      if (this.reviewed) {
        if (this.selectedReviewers.length < this.members.length) {
          chips.push({label: this.$t('reviewers'), onRemove: () => {
            this.selectedReviewers = [...this.members];
          }});
        }
      } else if (this.selectedMembers.length < this.filteredMembers.length) {
        chips.push({label: this.$t('members'), onRemove: () => {
          this.selectedMembers = [...this.filteredMembers];
        }});
      }
      if (this.isByImageGroup) {
        if (this.selectedImageGroups.length < this.imageGroups.length) {
          chips.push({label: this.$t('image-groups'), onRemove: () => {
            this.selectedImageGroups = [...this.imageGroups];
          }});
        }
      } else if (!this.tooManyImages && this.selectedImages.length < this.images.length) {
        chips.push({label: this.$t('images'), onRemove: () => {
          this.selectedImages = [...this.images];
        }});
      }
      if (this.fromDate) {
        chips.push({label: this.$t('from'), onRemove: () => {
          this.fromDate = null;
        }});
      }
      if (this.toDate) {
        chips.push({label: this.$t('to'), onRemove: () => {
          this.toDate = null;
        }});
      }
      return chips;
    },
    // Facets shown as inline dropdowns in the filter bar (Terms/Images/Members always,
    // Tracks/Tags optionally via the popover "show in filter bar" toggles).
    barFacets() {
      let facets = [
        {key: 'terms', label: this.$t('terms')},
        {key: 'images', label: this.$t('images')},
        {key: 'members', label: this.reviewed ? this.$t('reviewers') : this.$t('members')}
      ];
      if (this.extraBar.includes('tracks') && this.tracks.length) {
        facets.push({key: 'tracks', label: this.$t('tracks')});
      }
      if (this.extraBar.includes('tags')) {
        facets.push({key: 'tags', label: this.$t('tags')});
      }
      return facets;
    },
    caseActive() {
      return this.caseMatchedImages !== null;
    },
    // ----- selection / bulk -----
    selectionAnnots() {
      return Object.values(this.selectionMap);
    },
    selectionIds() {
      return Object.keys(this.selectionMap).map(id => Number(id));
    },
    selectionCount() {
      return this.selectionIds.length;
    },
    selectionTerms() {
      let terms = new Set();
      this.selectionAnnots.forEach(a => (a.term || []).forEach(t => terms.add(t)));
      return [...terms];
    },
    singleSelectionTerm() {
      return this.selectionTerms.length === 1 ? this.selectionTerms[0] : null;
    },
    setTermOptions() {
      // when the whole selection shares one term, offer the OTHER terms to reassign to
      return this.terms.filter(t => t.id !== this.singleSelectionTerm);
    },
    lightboxImageName() {
      if (!this.lightbox) {
        return '';
      }
      if (this.lightbox.instanceFilename) {
        return this.lightbox.instanceFilename;
      }
      let image = this.images.find(img => img.id === this.lightbox.image);
      if (!image) {
        return '';
      }
      return (this.blindMode && image.blindedName) ? image.blindedName : image.instanceFilename;
    }
  },
  methods: {
    scrollHandler: _.debounce(function () {
      let scrollBlock = this.$refs.listAnnots;
      let actualScrollPos = scrollBlock.scrollTop + scrollBlock.clientHeight;

      if (actualScrollPos === scrollBlock.scrollHeight && !this.areAllOptionsLoaded) {
        console.log('Loading new categories from scroll handler.');
        this.loadCategories();
      }
    }, 100),
    loadCategories() {
      const newCount = this.limitedCategoryOptions.length + categoryBatch;
      if (newCount >= this.categoryOptions.length) {
        this.nLoadedOptionsPerCategory[this.selectedCategorization.categorization] = this.categoryOptions.length;
      } else {
        this.nLoadedOptionsPerCategory[this.selectedCategorization.categorization] = newCount;
      }
    },
    appendShortTermToken,
    viewAnnot(data) {
      this.$router.push(`/project/${this.project.id}/image/${data.annot.image}/annotation/${data.annot.id}`);
    },

    // Reset every filter back to "all" / defaults (the Reset button + popover "Reset all").
    resetFilters() {
      this.resetPagesAndFilters();
      this.selectedAnnotationType = this.userAnnotationOption;
      this.selectedTermsIds = [...this.termOptionsIds];
      this.selectedTracksIds = [...this.trackOptionsIds];
      this.selectedTags = [...this.tagsOptions];
      this.selectedMembers = [...this.filteredMembers];
      this.selectedReviewers = [...this.members];
      if (!this.tooManyImages) {
        this.selectedImages = [...this.images];
      }
      this.selectedImageGroups = [...this.imageGroups];
      this.fromDate = null;
      this.toDate = null;
    },

    // Tile click opens the crop lightbox (instead of jumping straight to the viewer).
    openLightbox(data) {
      this.lightbox = data.annot;
      this.lightboxCrop = null;
      this.fetchCrop(data.annot);
    },
    async fetchCrop(annot) {
      try {
        let url = annot.annotationCropURL(512, 'jpg', {
          square: true,
          complete: true,
          thickness: 2,
          increaseArea: 1.25,
          draw: true
        });
        let response = await Ims.instance.api.get(url, {responseType: 'blob'});
        if (this.lightbox && this.lightbox.id === annot.id) {
          this.lightboxCrop = URL.createObjectURL(response.data);
        }
      } catch (error) {
        console.log(error);
      }
    },
    closeLightbox() {
      if (this.lightboxCrop) {
        URL.revokeObjectURL(this.lightboxCrop);
      }
      this.lightbox = null;
      this.lightboxCrop = null;
    },
    openInSlide() {
      let annot = this.lightbox;
      this.closeLightbox();
      this.viewAnnot({annot});
    },
    deleteFromLightbox() {
      let annot = this.lightbox;
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-deletion-annotation'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            await annot.delete();
            this.closeLightbox();
            this.revision++;
          } catch (error) {
            this.$notify({type: 'error', text: this.$t('notif-error-annotation-deletion')});
          }
        }
      });
    },
    // ===== UI-10: inline filter-bar dropdowns =====
    toggleBarOpen(key) {
      if (this.openBar === key) {
        this.openBar = null;
        return;
      }
      this.openBar = key;
      this.barSearch = '';
      this.loadFacetCounts(key);
    },
    closeBar() {
      this.openBar = null;
    },
    // Raw option list for a facet, honouring the search box and (for images) the
    // case-info finder. Each option: {id, name, sub}.
    barOptions(key) {
      let opts = [];
      if (key === 'terms') {
        opts = [...this.terms, this.noTermOption].map(t => ({id: t.id, name: t.name, sub: ''}));
      } else if (key === 'images') {
        let imgs = this.caseActive ? this.caseMatchedImages : this.images;
        opts = imgs.map(img => ({
          id: img.id,
          name: (this.blindMode && img.blindedName) ? img.blindedName : img.instanceFilename,
          sub: this.caseSubLabel(img)
        }));
      } else if (key === 'members') {
        let users = this.reviewed ? this.members : this.filteredMembers;
        opts = users.map(u => ({id: u.id, name: u.fullName, sub: ''}));
      } else if (key === 'tracks') {
        opts = this.filteredTracks.map(t => ({id: t.id, name: t.name, sub: ''}));
      } else if (key === 'tags') {
        opts = [...this.tags, this.noTagOption].map(t => ({id: t.id, name: t.name, sub: ''}));
      }
      let q = this.barSearch.trim().toLowerCase();
      if (q) {
        opts = opts.filter(o => o.name && o.name.toLowerCase().includes(q));
      }
      return opts;
    },
    caseSubLabel(img) {
      let ci = img.caseInfo || {};
      if (!ci.caseId && !ci.specimenId) {
        return '';
      }
      return `${this.$t('case-information-field-case-id')}: ${ci.caseId || '—'} · ${this.$t('case-information-field-specimen-id')}: ${ci.specimenId || '—'}`;
    },
    barSelectedIds(key) {
      switch (key) {
        case 'terms': return this.selectedTermsIds;
        case 'images': return this.selectedImagesIds;
        case 'members': return (this.reviewed ? this.selectedReviewers : this.selectedMembers).map(u => u.id);
        case 'tracks': return this.selectedTracksIds;
        case 'tags': return this.selectedTagsIds;
        default: return [];
      }
    },
    barChecked(key, id) {
      return this.barSelectedIds(key).includes(id);
    },
    barMasterState(key) {
      let total = this.barOptions(key).length;
      let sel = this.barOptions(key).filter(o => this.barChecked(key, o.id)).length;
      if (sel === 0) {
        return 'none';
      }
      return (sel >= total) ? 'all' : 'some';
    },
    barSummary(key) {
      let state = this.barMasterState(key);
      if (state === 'all') {
        return this.$t('all');
      }
      if (state === 'none') {
        return this.$t('none-selected');
      }
      return this.barOptions(key).filter(o => this.barChecked(key, o.id)).length + ' ' + this.$t('selected');
    },
    barToggle(key, id) {
      if (key === 'terms') {
        this.selectedTermsIds = this.toggleInArray(this.selectedTermsIds, id);
      } else if (key === 'tracks') {
        this.selectedTracksIds = this.toggleInArray(this.selectedTracksIds, id);
      } else if (key === 'images') {
        this.selectedImages = this.toggleObjInArray(this.selectedImages, this.findImage(id));
      } else if (key === 'members') {
        if (this.reviewed) {
          this.selectedReviewers = this.toggleObjInArray(this.selectedReviewers, this.members.find(u => u.id === id));
        } else {
          this.selectedMembers = this.toggleObjInArray(this.selectedMembers, this.filteredMembers.find(u => u.id === id));
        }
      } else if (key === 'tags') {
        this.selectedTags = this.toggleObjInArray(this.selectedTags, this.tagsOptions.find(t => t.id === id));
      }
    },
    barMaster(key) {
      let allOn = this.barMasterState(key) === 'all';
      let ids = allOn ? [] : this.barOptions(key).map(o => o.id);
      if (key === 'terms') {
        this.selectedTermsIds = ids;
      } else if (key === 'tracks') {
        this.selectedTracksIds = ids;
      } else if (key === 'images') {
        this.selectedImages = allOn ? [] : this.barOptions('images').map(o => this.findImage(o.id)).filter(Boolean);
      } else if (key === 'members') {
        let users = allOn ? [] : (this.reviewed ? [...this.members] : [...this.filteredMembers]);
        if (this.reviewed) {
          this.selectedReviewers = users;
        } else {
          this.selectedMembers = users;
        }
      } else if (key === 'tags') {
        this.selectedTags = allOn ? [] : [...this.tagsOptions];
      }
    },
    toggleInArray(arr, id) {
      return arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
    },
    toggleObjInArray(arr, obj) {
      if (!obj) {
        return arr;
      }
      return arr.some(o => o.id === obj.id) ? arr.filter(o => o.id !== obj.id) : [...arr, obj];
    },
    findImage(id) {
      return this.images.find(i => i.id === id)
        || (this.caseMatchedImages || []).find(i => i.id === id);
    },

    // ----- case-info finder (Images dropdown) -----
    effectiveCaseCriteria() {
      let crit = this.caseCriteria.slice();
      if (this.caseValue.trim()) {
        crit.push({field: this.caseField, value: this.caseValue.trim()});
      }
      return crit;
    },
    addCaseCriterion() {
      if (!this.caseValue.trim()) {
        return;
      }
      this.caseCriteria = [...this.caseCriteria, {field: this.caseField, value: this.caseValue.trim()}];
      this.caseValue = '';
      this.fetchCaseImages();
    },
    removeCaseCriterion(index) {
      this.caseCriteria = this.caseCriteria.filter((_, i) => i !== index);
      this.fetchCaseImages();
    },
    caseFieldLabel(field) {
      let f = this.caseFields.find(c => c.val === field);
      return f ? f.label : field;
    },
    async fetchCaseImages() {
      let crit = this.caseCriteria;
      if (!crit.length) {
        this.caseMatchedImages = null;
        return;
      }
      this.caseLoading = true;
      try {
        let expr = crit.map(c => ({field: c.field, operator: 'contains', value: c.value, conj: 'and'}));
        let url = 'project/' + this.project.id + '/imageinstance.json';
        let data = (await Ims.instance.api.get(url, {params: {filterExpression: JSON.stringify(expr), max: 200}})).data;
        this.caseMatchedImages = (data.collection || []).map(j => {
          let img = new ImageInstance(j);
          img.caseInfo = j.caseInfo || {};
          return img;
        });
      } catch (error) {
        console.log(error);
        this.caseMatchedImages = [];
      }
      this.caseLoading = false;
      if (this.openBar === 'images') {
        this.loadFacetCounts('images');
      }
    },

    // ----- smart-capped faceted counts -----
    facetKey(key, id) {
      return key + ':' + id;
    },
    facetCountText(key, id) {
      let v = this.facetCounts[this.facetKey(key, id)];
      return (v === undefined) ? '' : String(v);
    },
    async loadFacetCounts(key) {
      let opts = this.barOptions(key);
      let cap = (key === 'images') ? 30 : 40;
      if (opts.length === 0 || opts.length > cap) {
        return; // smart cap: skip counts for very large option lists
      }
      this.facetToken++;
      let token = this.facetToken;
      let entries = await Promise.all(opts.map(async o => {
        try {
          let count = (await this.countCollection(key, o.id).fetchPage(0)).totalNbItems;
          return [this.facetKey(key, o.id), count];
        } catch (error) {
          return [this.facetKey(key, o.id), undefined];
        }
      }));
      if (token !== this.facetToken) {
        return; // a newer load superseded this one
      }
      let fc = {...this.facetCounts};
      entries.forEach(([k, v]) => {
        if (v !== undefined) {
          fc[k] = v;
        }
      });
      this.facetCounts = fc;
    },
    // An AnnotationCollection mirroring the current filters but restricting facet `key`
    // to the single option `id`, used purely to read totalNbItems.
    countCollection(key, id) {
      let imagesIds = !(this.tooManyImages && this.selectedImages.length === 0) ? this.selectedImagesIds : null;
      let params = {
        project: this.project.id,
        terms: this.selectedTermsIds.length === this.termsOptions.length ? null : this.selectedTermsIds.filter(t => t > 0),
        images: imagesIds,
        users: this.selectedUsersIds,
        reviewed: this.reviewed,
        reviewUsers: this.reviewUsersIds,
        noTerm: this.noTerm,
        afterThan: this.afterThan,
        beforeThan: this.beforeThan
      };
      if (key === 'terms') {
        params.terms = (id > 0) ? [id] : null;
        params.noTerm = (id === this.noTermOption.id);
      } else if (key === 'images') {
        params.images = [id];
      } else if (key === 'members') {
        params.users = this.reviewed ? null : [id];
        params.reviewUsers = this.reviewed ? [id] : null;
      } else if (key === 'tracks') {
        params.tracks = (id > 0) ? [id] : null;
        params.noTrack = (id === this.noTrackOption.id);
      } else if (key === 'tags') {
        params.tags = (id > 0) ? [id] : null;
        params.noTag = (id === this.noTagOption.id);
      }
      let collection = new AnnotationCollection(params);
      collection.max = 1;
      return collection;
    },
    toggleExtraBar(key) {
      if (this.extraBar.includes(key)) {
        this.extraBar = this.extraBar.filter(k => k !== key);
      } else {
        this.extraBar = [...this.extraBar, key];
      }
    },

    // ===== UI-10 Group B: selection + bulk actions =====
    toggleSelect(annot) {
      let map = {...this.selectionMap};
      if (map[annot.id]) {
        delete map[annot.id];
      } else {
        map[annot.id] = annot;
      }
      this.selectionMap = map;
    },
    toggleGroupSelect(annots) {
      let map = {...this.selectionMap};
      let allSelected = annots.every(a => map[a.id]);
      annots.forEach(a => {
        if (allSelected) {
          delete map[a.id];
        } else {
          map[a.id] = a;
        }
      });
      this.selectionMap = map;
    },
    clearSelection() {
      this.selectionMap = {};
      this.setTermOpen = false;
      this.tagOpen = false;
      this.bulkExportOpen = false;
    },
    // single-annotation delete (tile / lightbox hover action)
    deleteAnnotation(annot) {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-deletion-annotation'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            await annot.delete();
            this.removeFromSelection(annot.id);
            this.revision++;
          } catch (error) {
            this.$notify({type: 'error', text: this.$t('notif-error-annotation-deletion')});
          }
        }
      });
    },
    removeFromSelection(id) {
      if (this.selectionMap[id]) {
        let map = {...this.selectionMap};
        delete map[id];
        this.selectionMap = map;
      }
    },
    bulkDelete() {
      let annots = this.selectionAnnots;
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-deletion-annotation'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: async () => {
          try {
            await Promise.all(annots.map(a => a.delete()));
            this.clearSelection();
            this.revision++;
            this.$notify({type: 'success', text: `${annots.length} ${this.$t('annotations')}`});
          } catch (error) {
            this.$notify({type: 'error', text: this.$t('notif-error-annotation-deletion')});
          }
        }
      });
    },
    async bulkSetTerm(term) {
      let annots = this.selectionAnnots;
      try {
        await Promise.all(annots.map(async a => {
          // remove existing terms set by the current user, then add the new one
          let existing = (a.userByTerm || [])
            .filter(ubt => ubt.user && ubt.user.includes(this.currentUser.id))
            .map(ubt => ubt.term);
          await Promise.all(existing.map(t => AnnotationTerm.delete(a.id, t, this.currentUser.id).catch(() => {})));
          await new AnnotationTerm({annotation: a.id, term: term.id}).save();
        }));
        this.setTermOpen = false;
        this.clearSelection();
        this.revision++;
        this.$notify({type: 'success', text: `${annots.length} → ${term.name}`});
      } catch (error) {
        this.$notify({type: 'error', text: this.$t('notif-error-add-term')});
      }
    },
    async bulkAddTag(tag) {
      let annots = this.selectionAnnots;
      try {
        await Promise.all(annots.map(a => new TagDomainAssociation({tag: tag.id}, a).save().catch(() => {})));
        this.tagOpen = false;
        this.clearSelection();
        this.$notify({type: 'success', text: `${annots.length} · ${tag.name}`});
      } catch (error) {
        this.$notify({type: 'error', text: this.$t('error')});
      }
    },
    // Export ONLY the selected annotations, in the chosen format (the backend now accepts an
    // explicit annotation id list). Keeps the selection so the user can export several formats.
    async bulkExport(format) {
      this.bulkExportOpen = false;
      await this.download(format, this.selectionIds);
    },

    async fetchImages() {
      if (!this.tooManyImages) {
        this.images = (await ImageInstanceCollection.fetchAll({
          filterKey: 'project',
          filterValue: this.project.id,
          light: true
        })).array;
      }
    },
    async fetchImageGroups() {
      this.imageGroups = (await ImageGroupCollection.fetchAll({
        filterKey: 'project',
        filterValue: this.project.id
      })).array;
    },
    async fetchUsers() {

      let collection = new UserCollection({
        filterKey: 'project',
        filterValue: this.project.id,
      });

      this.projectUsers = (await collection.fetchAll()).array;
    },
    async fetchTracks() {
      this.tracks = (await TrackCollection.fetchAll({filterKey: 'project', filterValue: this.project.id})).array;
    },
    async fetchTags() {
      this.tags = (await TagCollection.fetchAll()).array;
    },
    async download(format, annotationIds = null) {
      if (this.exporting) {
        return; // a download is already running — the overlay blocks the UI meanwhile
      }
      this.exporting = true;
      try {
        await this.collection.download(format, annotationIds);
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-export')});
      } finally {
        this.exporting = false;
      }
    },
    addTerm(term) {
      this.terms.push(term);
      this.selectedTermsIds.push(term.id);
    },
    addTrack(track) {
      this.tracks.push(track);
    },
    resetPagesAndFilters() {
      this.$store.commit(this.storeModule + '/resetPagesAndFilters');
    },
    showList(prop) {
      switch (this.selectedCategorization.categorization) {
        case 'TERM':
          return this.selectedTermsIds.includes(prop.id);
        case 'IMAGE':
          return this.selectedImagesIds.includes(prop.id);
        case 'USER':
          return this.reviewed ? this.reviewUsersIds.includes(prop.id) : this.selectedUsersIds.includes(prop.id);
        case 'TRACK':
          return this.selectedTracksIds.includes(prop.id);
        case 'IMAGEGROUP':
          return this.selectedImageGroupsIds.includes(prop.id);
        /**
         * We will only have one ListAnnotationBy component rendered
         * when the selectedCategorization is uncategorized.
         * Removing or adding terms, images, etc,
         * in the filters shouldn't change the component visibility.
         */
        case 'UNCATEGORIZED':
          return true;
      }
    },
    imagesIdsInGroup(group) {
      return group.imageInstances.map(image => image.id);
    }
  },
  watch: {
    querySearchTags(values) {
      if (values) {
        this.selectedTags = [];
        let queriedTags = this.availableTags.filter(tag => values.split(',').includes(tag.name));
        if (queriedTags) {
          this.resetPagesAndFilters(); // we want all annotations of the job => reset state
          this.selectedTags = queriedTags;
        }
      }
    },
    querySearchImage(val) {
      if (val) {
        this.selectedImages = [];
        let queriedImage = this.images.find(image => image.id === Number(val));
        if (queriedImage) {
          this.resetPagesAndFilters(); // we want all annotations of the image => reset state
          this.selectedImages = [queriedImage];
        }
      }
    }
  },
  async created() {
    this.annotationTypes = [this.userAnnotationOption, this.reviewedAnnotationOption];

    // if store was not yet initialized, set default values
    if (!this.selectedSize) {
      this.selectedSize = this.allowedSizes[0];
    }
    if (!this.selectedCategorization) {
      this.selectedCategorization = this.allowedCategorizations[0];
    }
    if (!this.selectedColor) {
      this.selectedColor = this.colors[0];
    }
    if (!this.selectedAnnotationType) {
      this.selectedAnnotationType = this.userAnnotationOption;
    }
    if (!this.regroup) {
      this.regroup = this.groupBundling[this.groupBundling.length - 1];
    }
    // ---

    try {
      await Promise.all([
        this.fetchImages(),
        this.fetchImageGroups(),
        this.fetchUsers(),
        this.fetchTracks(),
        this.fetchTags()
      ]);
    } catch (error) {
      console.log(error);
      this.error = true;
      return;
    }

    if (this.targetAnnotationType) {
      this.resetPagesAndFilters(); // we want all annotations of this type => reset state
      this.selectedAnnotationType = this.targetAnnotationType;
    }

    if (this.$route.query.image) {
      let queriedImage = this.images.find(image => image.id === Number(this.$route.query.image));
      if (this.tooManyImages) {
        queriedImage = await ImageInstance.fetch(Number(this.$route.query.image));
      }
      if (queriedImage) {
        this.resetPagesAndFilters(); // we want all annotations of the image => reset state
        this.selectedImages = [queriedImage];
      }
    }
    if (this.$route.query.tags) {
      let queriedTags = this.availableTags.filter(tag => this.$route.query.tags.split(',').includes(tag.name));
      if (queriedTags) {
        this.resetPagesAndFilters(); // we want all annotations of the tags => reset state
        this.selectedTags = queriedTags;
      }
    }

    this.loading = false;
  }
};
</script>

<style scoped>
/* UI-9: ZayaAI annotations page — filters behind a button, crop lightbox.
   Design ref: ims-annotation-panel "Annotations Panel". */
.annots-page {
  background: #fff;
  min-height: calc(100vh - 50px);
  padding: 12px 26px 48px;
  position: relative;
}

/* ---- active filter chips ---- */
.az-chips {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 6px 0 12px;
}
.az-chips-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #9aa3b0;
  text-transform: uppercase;
}
.az-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #edf1fe;
  border: 1px solid #dbe3fb;
  color: #1e63d0;
  font-size: 12.5px;
  font-weight: 600;
  padding: 5px 8px 5px 11px;
  border-radius: 8px;
}
.az-chip-x {
  cursor: pointer;
  display: flex;
  color: #7e97e0;
  font-size: 11px;
}
.az-chip-x:hover { color: #1e63d0; }

/* ---- sticky filter bar ---- */
.az-bar {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #eceef2;
  padding: 11px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}
.az-filters-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  height: 38px;
  padding: 0 15px;
  border-radius: 10px;
  border: 1px solid #1e63d0;
  background: #1e63d0;
  color: #fff;
  font-weight: 700;
  font-size: 13.5px;
  cursor: pointer;
}
.az-filters-btn:hover { background: #1750a8; }
.az-badge {
  background: #fff;
  color: #1e63d0;
  font-size: 11px;
  font-weight: 800;
  min-width: 19px;
  height: 19px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}
.az-reset-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #e6e9ef;
  background: #fff;
  color: #5a6473;
  font-weight: 600;
  font-size: 13.5px;
  cursor: pointer;
}
.az-reset-btn:hover { background: #f4f6fa; }
.az-bar-spacer { flex: 1; }
.az-bar-div { width: 1px; height: 26px; background: #e6e9ef; flex: none; }
.az-count {
  font-size: 13px;
  color: #8a93a3;
  font-weight: 600;
  white-space: nowrap;
}

/* ---- inline facet dropdowns ---- */
.az-bar-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}
.az-bardd { position: relative; flex: none; }
.az-bardd-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e2e6ec;
  background: #fff;
  color: #3a4252;
  font-size: 13.5px;
  cursor: pointer;
}
.az-bardd-btn.open { border-color: #1e63d0; background: #f4f7fe; }
.az-bardd-label { font-weight: 700; }
.az-bardd-pill {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  background: #f0f2f6;
  color: #8a93a3;
}
.az-bardd-pill.some { background: #e1e9fd; color: #1e63d0; }
.az-bardd-caret { font-size: 11px; color: #9aa3b0; }
.az-bardd-pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 25;
  width: 320px;
  border: 1px solid #e8eaef;
  border-radius: 13px;
  background: #fff;
  padding: 6px;
  max-height: 420px;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-shadow: 0 16px 40px rgba(20, 30, 55, 0.18);
}
.az-bardd-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  border: 1px solid #e6e9ef;
  border-radius: 9px;
  padding: 0 10px;
  margin: 3px 4px 8px;
  color: #9aa3b0;
}
.az-bardd-search input { border: none; background: transparent; font-size: 13.5px; color: #2a3342; width: 100%; outline: none; }

/* option rows + checkboxes */
.az-opt {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.az-opt:hover { background: #f4f6fa; }
.az-master { background: #f7f8fb; }
.az-master:hover { background: #eff2f7; }
.az-check {
  width: 19px;
  height: 19px;
  border-radius: 6px;
  flex: none;
  border: 1.7px solid #cbd2dc;
  background: #fff;
  position: relative;
}
.az-check.all { background: #1e63d0; border-color: #1e63d0; }
.az-check.all::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 1px;
  width: 6px;
  height: 11px;
  border: solid #fff;
  border-width: 0 2.5px 2.5px 0;
  transform: rotate(45deg);
}
.az-check.some { background: #1e63d0; border-color: #1e63d0; }
.az-check.some::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 8px;
  width: 9px;
  height: 2.6px;
  background: #fff;
  border-radius: 2px;
}
.az-opt-text { flex: 1; min-width: 0; }
.az-opt-name { display: block; font-size: 13.5px; font-weight: 500; color: #2a3342; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.az-opt-sub { display: block; font-size: 11px; color: #9aa3b0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.az-opt-count {
  font-size: 12px;
  color: #9aa3b0;
  font-variant-numeric: tabular-nums;
  background: #f0f2f6;
  padding: 2px 8px;
  border-radius: 12px;
}
.az-opt-empty { padding: 14px 10px; text-align: center; font-size: 13px; color: #9aa3b0; }

/* case-info finder */
.az-case { padding: 0 4px 7px; }
.az-case-head {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 9px;
  border-radius: 8px;
  cursor: pointer;
  background: #f4f6fa;
  font-size: 12px;
  font-weight: 700;
  color: #5a6473;
}
.az-case-head:hover { background: #eaeef5; }
.az-case-head .fas { font-size: 11px; }
.az-case-active { color: #1e63d0; }
.az-case-body { padding: 7px 2px 0; }
.az-case-row { display: flex; gap: 6px; align-items: center; }
.az-case-row .select, .az-case-row .select select { font-size: 12px; }
.az-case-val {
  flex: 1;
  min-width: 0;
  height: 32px;
  border: 1px solid #e2e6ec;
  border-radius: 8px;
  padding: 0 9px;
  font-size: 12.5px;
  color: #2a3342;
  outline: none;
}
.az-case-add {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #1e63d0;
  background: #1e63d0;
  color: #fff;
  cursor: pointer;
}
.az-case-add:hover { background: #1750a8; }
.az-case-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
.az-case-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #edf1fe;
  border: 1px solid #dbe3fb;
  color: #1e63d0;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 6px 3px 9px;
  border-radius: 7px;
}
.az-case-chip .k { opacity: 0.7; }
.az-case-chip .x { cursor: pointer; display: flex; }
.az-case-loading { padding: 8px; text-align: center; color: #9aa3b0; }

/* show-in-filter-bar toggles */
.az-showbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: #fbfbfd;
  border: 1px solid #eceef2;
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 16px;
}
.az-showbar-label { font-size: 11px; font-weight: 700; letter-spacing: 0.04em; color: #7a8494; text-transform: uppercase; }
.az-showbar-tog {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 11px;
  border-radius: 9px;
  border: 1px solid #e2e6ec;
  background: #fff;
  color: #3a4252;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.az-showbar-tog .fas { font-size: 10px; }
.az-showbar-tog.on { background: #edf1fe; border-color: #1e63d0; color: #1e63d0; }
.az-showbar-tog.locked { background: #edf1fe; border-color: #dbe3fb; color: #1e63d0; cursor: default; }

/* ---- groups list ---- */
.az-groups {
  padding-top: 16px;
}
.az-empty-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 16px;
  text-align: center;
}
.az-empty-title {
  margin: 0;
  font-size: 14.5px;
  color: #94a3b8;
  font-weight: 500;
}
.az-loadmore {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px auto 0;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid #e6e9ef;
  background: #fff;
  color: #5a6473;
  font-weight: 600;
  cursor: pointer;
}
.az-loadmore:hover { background: #f4f6fa; }

/* ---- export results ---- */
/* blocking overlay while a PDF/CSV/XLS export is generated */
.az-export-overlay {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}
.az-export-card {
  background: #fff;
  border-radius: 16px;
  padding: 30px 38px;
  box-shadow: 0 30px 80px -20px rgba(15, 23, 42, 0.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.az-export-card .fa-circle-notch {
  font-size: 30px;
  color: #1e63d0;
}
.az-export-overlay-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}
.az-export-overlay-sub {
  font-size: 13px;
  color: #64748b;
  max-width: 280px;
}

.az-export {
  border: 1px solid #e8eaef;
  border-radius: 14px;
  padding: 14px 18px;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 18px;
}
.az-export-info {
  display: flex;
  align-items: center;
  gap: 11px;
  flex: 1;
  min-width: 180px;
}
.az-export-ico {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #edf1fe;
  color: #1e63d0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.az-export-title { font-size: 14px; font-weight: 800; color: #1a2233; }
.az-export-sub { font-size: 12px; color: #9aa3b0; font-weight: 500; }
.az-export-menu { position: relative; }
.az-export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #1e63d0;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 9px 14px;
  font-weight: 700;
  font-size: 13.5px;
  cursor: pointer;
}
.az-export-btn:hover { background: #1750a8; }
.az-export-caret { font-size: 11px; opacity: 0.85; }
.az-export-pop {
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 5;
  background: #fff;
  border: 1px solid #e8eaef;
  border-radius: 13px;
  box-shadow: 0 16px 40px rgba(20, 30, 55, 0.18);
  padding: 8px;
  display: flex;
  gap: 8px;
}
.az-fmt {
  min-width: 52px;
  height: 44px;
  border-radius: 9px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.03em;
  color: #fff;
}
.az-fmt.az-pdf { background: #e0567a; }
.az-fmt.az-pdf:hover { background: #d23f66; }
.az-fmt.az-csv { background: #2fa39b; }
.az-fmt.az-csv:hover { background: #289089; }
.az-fmt.az-xls { background: #1f8a5b; }
.az-fmt.az-xls:hover { background: #197a50; }

/* ---- filters popover ---- */
.az-pop-scrim {
  position: fixed;
  inset: 0;
  z-index: 58;
  background: rgba(17, 23, 38, 0.12);
}
.az-pop {
  position: fixed;
  top: 104px;
  right: 26px;
  width: 680px;
  max-width: calc(100vw - 40px);
  max-height: 76vh;
  z-index: 60;
  background: #fff;
  border: 1px solid #e8eaef;
  border-radius: 18px;
  box-shadow: 0 26px 70px rgba(20, 30, 55, 0.24);
  display: flex;
  flex-direction: column;
}
.az-pop-head {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 16px 20px;
  border-bottom: 1px solid #eef0f4;
}
.az-pop-head-ico { color: #1e63d0; }
.az-pop-head h2 { margin: 0; font-size: 16px; font-weight: 800; color: #1a2233; flex: 1; }
.az-pop-reset {
  background: transparent;
  border: none;
  color: #1e63d0;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
}
.az-pop-reset:hover { background: #edf1fe; }
.az-pop-x {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #eceef2;
  background: #fff;
  color: #5a6473;
  cursor: pointer;
}
.az-pop-x:hover { background: #f4f6fa; }
.az-pop-body { flex: 1; overflow-y: auto; padding: 18px 20px; }
.az-section-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #9aa3b0;
  text-transform: uppercase;
  margin-bottom: 12px;
}
.az-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 13px;
}
.az-field label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #7a8494;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.az-divider { height: 1px; background: #eef0f4; margin: 18px 0; }
.az-pop-foot {
  flex: none;
  border-top: 1px solid #eef0f4;
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.az-pop-reset2 {
  background: transparent;
  border: none;
  color: #7a8494;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 4px;
}
.az-pop-reset2:hover { color: #1e63d0; }
.az-pop-done {
  height: 42px;
  padding: 0 22px;
  border-radius: 11px;
  border: none;
  background: #1e63d0;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
}
.az-pop-done:hover { background: #1750a8; }

/* ---- lightbox (crop popup) ---- */
.az-light-scrim {
  position: fixed;
  inset: 0;
  background: rgba(15, 20, 35, 0.7);
  backdrop-filter: blur(4px);
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
}
.az-light {
  display: flex;
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  max-width: 820px;
  width: 100%;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
}
.az-light-img {
  flex: 1;
  min-width: 0;
  background: #0f1423;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  min-height: 320px;
}
.az-light-img img { width: 100%; max-width: 440px; border-radius: 10px; display: block; }
.az-light-spin { color: #6b7480; font-size: 26px; }
.az-light-side {
  width: 300px;
  flex: none;
  display: flex;
  flex-direction: column;
}
.az-light-head {
  padding: 18px 20px;
  border-bottom: 1px solid #eef0f4;
  display: flex;
  align-items: center;
  gap: 10px;
}
.az-light-title { flex: 1; font-size: 15px; font-weight: 800; color: #1a2233; }
.az-light-x {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid #eceef2;
  background: #fff;
  color: #5a6473;
  cursor: pointer;
}
.az-light-x:hover { background: #f4f6fa; }
.az-light-meta { flex: 1; overflow-y: auto; padding: 14px 20px; }
.az-light-meta .row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #f2f4f7;
}
.az-light-meta .k { font-size: 12.5px; color: #9aa3b0; font-weight: 600; }
.az-light-meta .v { font-size: 13px; color: #2a3342; font-weight: 600; text-align: right; }
.az-light-foot {
  flex: none;
  border-top: 1px solid #eef0f4;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.az-light-del {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  color: #d8456a;
  border: 1px solid #f3d2db;
  border-radius: 10px;
  padding: 10px 13px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}
.az-light-del:hover { background: #fdf3f6; border-color: #e0567a; }
.az-light-open {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #1e63d0;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 15px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}
.az-light-open:hover { background: #1750a8; }

@media (max-width: 640px) {
  .az-light { flex-direction: column; }
  .az-light-side { width: 100%; }
}

/* ---- bulk selection action bar ---- */
.az-selbar {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #1a2233;
  color: #fff;
  border-radius: 14px;
  padding: 9px 10px 9px 16px;
  box-shadow: 0 14px 40px rgba(15, 20, 35, 0.35);
}
.az-selcount { font-weight: 700; font-size: 14px; }
.az-seldiv { width: 1px; height: 22px; background: rgba(255, 255, 255, 0.18); margin: 0 6px; }
.az-selact {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 600;
  font-size: 13.5px;
  padding: 8px 11px;
  border-radius: 9px;
  cursor: pointer;
}
.az-selact:hover { background: rgba(255, 255, 255, 0.12); }
.az-selact.danger { color: #ff8fa8; }
.az-selact.danger:hover { background: rgba(255, 90, 120, 0.16); }
.az-selx {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  cursor: pointer;
}
.az-selx:hover { background: rgba(255, 255, 255, 0.2); }
.az-selwrap {
  position: relative;
  display: inline-flex;
}
.az-selpop {
  position: absolute;
  left: 0;
  bottom: calc(100% + 14px);
  background: #fff;
  color: #1a2233;
  border: 1px solid #e8eaef;
  border-radius: 13px;
  box-shadow: 0 16px 40px rgba(15, 20, 35, 0.28);
  padding: 7px;
  min-width: 220px;
  max-height: 270px;
  overflow-y: auto;
}
.az-selpop-title {
  position: sticky;
  top: 0;
  background: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  color: #9aa3b0;
  text-transform: uppercase;
  padding: 6px 9px 7px;
}
.az-selpop-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 9px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  color: #2a3342;
}
.az-selpop-row:hover { background: #f4f6fa; }
.az-selpop-dot { width: 10px; height: 10px; border-radius: 50%; flex: none; }
.az-selpop-empty { padding: 10px; color: #9aa3b0; font-size: 13px; }
</style>
